import {ChangeDetectionStrategy, Component, ElementRef, inject, Input, ViewChild,} from '@angular/core';
import {CommonModule} from '@angular/common';
import {NgxChartsModule} from '@swimlane/ngx-charts';
import {map, Observable, of, switchMap} from 'rxjs';
import {CampaignStoreService} from '@/app/features/campaña/services/campaign-store.service';
import {AnalysisService} from '@/app/features/reportes/services/analysis.service';
import {Risk, RISK_COLOR, RISK_ORDER} from '@/app/utils/risk-colors';
import {getContrastingTextColor} from "@/app/utils/color.utils";

type ApiRow = {
  domainName?: string;
  averageScore?: number;
  rating?: string | Risk;
  totalRespondents?: number;
};

type RowVM = {
  name: string; // dominio
  score: number; // promedio del dominio para ese nivel
  rating: Risk; // nivel
  respondents: number; // personas (suma por dominio+nivel tras fusión)
  idx: number; // índice dentro del dominio (1..n) según severidad
};

@Component({
  selector: 'app-scores-section',
  standalone: true,
  imports: [CommonModule, NgxChartsModule],
  templateUrl: './scores-section.component.html',
  styleUrls: ['./scores-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScoresSectionComponent {
  private store = inject(CampaignStoreService);
  private analysis = inject(AnalysisService);
  private el = inject(ElementRef);

  @Input() title = 'Resultados por dominio';
  @Input() guideId = 'guia-iii';

  // Labels for table headers, with default values for backward compatibility.
  @Input() domainLabel: string = 'Dominio';
  @Input() peopleLabel: string = 'Personas';
  @Input() levelLabel: string = 'Nivel';

  @ViewChild('chartContainer', {static: false})
  chartContainer!: ElementRef<HTMLDivElement>;

  private fullDomainNames: Map<string, string> = new Map();

  rows$: Observable<RowVM[]> = this.store.selectedCampaign$.pipe(
    switchMap((camp) => {
      if (!camp) return of<RowVM[]>([]);
      const baseReq = { campaign: camp.value as string, guideId: this.guideId };
      return this.analysis.getDomainScores(baseReq as any).pipe(
        map((rows: ApiRow[] | null) => {
          const capitalizedNames = (rows ?? []).map((r) => {
            r.domainName = this.capitalizeFirstLetter(r.domainName ?? '');
            return r;
          });
          return this.toVM(capitalizedNames);
        })
      );
    })
  );

  private readonly ratingOrder: Risk[] = RISK_ORDER.filter(
    (r) => r !== 'Nulo o despreciable'
  )
    .slice()
    .reverse();

  readonly ratingColors = this.ratingOrder.map((risk) => ({
    name: risk,
    value: RISK_COLOR[risk],
  }));

  tickFmt = (name: string) => {
    return this.capitalizeFirstLetter(name);
  };

  dlFmt = (value: any, seriesName?: string, item?: any): string => {
    const c = item?.data;
    if (!c?.series) return '';
    let last = -1;
    for (let i = c.series.length - 1; i >= 0; i--) {
      if (c.series[i].value > 0) {
        last = i;
        break;
      }
    }
    const idx = c.series.findIndex(
      (s: any) => s.name === seriesName && s.value === value
    );
    return idx === last && c._avg != null ? c._avg.toFixed(2) : '';
  };

  groupedChart$ = this.rows$.pipe(
    map((rows) => {
      const byDomain = new Map<string, RowVM[]>();
      for (const r of rows) {
        const arr = byDomain.get(r.name) ?? [];
        arr.push(r);
        byDomain.set(r.name, arr);
      }

      const severityIndex = (lvl: Risk) => this.ratingOrder.indexOf(lvl);
      const worstOf = (arr: RowVM[]) =>
        arr.reduce((m, r) => Math.max(m, severityIndex(r.rating)), -1);

      const domainNames = Array.from(byDomain.keys()).sort((a, b) => {
        const A = byDomain.get(a)!;
        const B = byDomain.get(b)!;
        const worstA = worstOf(A),
          worstB = worstOf(B);
        if (worstA !== worstB) return worstB - worstA;
        const totA = A.reduce((s, r) => s + (r.respondents || 0), 0);
        const totB = B.reduce((s, r) => s + (r.respondents || 0), 0);
        return totB - totA;
      });

      const chartData = domainNames.map((name) => {
        const arr = byDomain.get(name)!;

        const levelCount = new Map<Risk, number>();
        let sumW = 0,
          sumWX = 0;

        for (const r of arr) {
          const w = Number(r.respondents || 0);
          const x = Number(r.score || 0);
          levelCount.set(r.rating, (levelCount.get(r.rating) ?? 0) + w);
          sumW += w;
          sumWX += w * x;
        }

        const series = this.ratingOrder.map((lvl) => ({
          name: lvl,
          value: levelCount.get(lvl) ?? 0,
        }));

        const _avg = sumW > 0 ? sumWX / sumW : 0;

        return { name, series, _avg, _total: sumW };
      });

      chartData.forEach((item) => {
        this.fullDomainNames.set(this.tickFmt(item.name), item.name);
      });

      return chartData;
    })
  );

  private _groupByName(rows: ApiRow[]): Map<string, ApiRow[]> {
    const nameKey = 'domainName';
    const byName = new Map<string, ApiRow[]>();
    for (const r of rows ?? []) {
      const name = String((r as any)[nameKey] ?? '').trim();
      if (!name) continue;
      const arr = byName.get(name) ?? [];
      arr.push(r);
      byName.set(name, arr);
    }
    return byName;
  }

  tableRows$: Observable<RowVM[]> = this.store.selectedCampaign$.pipe(
    switchMap((camp) => {
      if (!camp) return of<RowVM[]>([]);
      const baseReq = { campaign: camp.value as string, guideId: this.guideId };
      return this.analysis.getDomainScores(baseReq as any).pipe(
        map((rows: ApiRow[] | null) => {
          const capitalizedNames = (rows ?? []).map((r) => {
            r.domainName = this.capitalizeFirstLetter(r.domainName ?? '');
            return r;
          });
          return this.toTableVM(capitalizedNames);
        })
      );
    })
  );

  private toVM(rows: ApiRow[]): RowVM[] {
    const byName = this._groupByName(rows);

    const order = (rt: Risk): number => this.ratingOrder.indexOf(rt);

    const out: RowVM[] = [];
    for (const [name, arr] of byName.entries()) {
      const byRating = new Map<
        Risk,
        { sumW: number; sumWX: number; count: number }
      >();

      for (const r of arr) {
        const risk = this.normalizeRisk(r.rating);
        const w = Number(r.totalRespondents ?? 0);
        const x = Number(r.averageScore ?? 0);
        const slot = byRating.get(risk) ?? { sumW: 0, sumWX: 0, count: 0 };
        slot.sumW += w;
        slot.sumWX += w * x;
        slot.count += 1;
        byRating.set(risk, slot);
      }

      const merged = Array.from(byRating.entries())
        .sort((a, b) => order(a[0]) - order(b[0]))
        .map(([risk, agg], idx) => {
          const score =
            agg.sumW > 0
              ? agg.sumWX / agg.sumW
              : arr
                .filter((r) => this.normalizeRisk(r.rating) === risk)
                .reduce((s, r) => s + Number(r.averageScore ?? 0), 0) /
              Math.max(1, agg.count);

          const respondents =
            agg.sumW > 0
              ? agg.sumW
              : arr
                .filter((r) => this.normalizeRisk(r.rating) === risk)
                .reduce((s, r) => s + Number(r.totalRespondents ?? 0), 0);

          const i = idx + 1;
          const vm: RowVM = {
            name,
            score: Number(score),
            rating: risk,
            respondents: Number(respondents),
            idx: i,
          };
          return vm;
        });

      out.push(...merged);
    }
    return out;
  }

  private normalizeRisk(s?: string | null): Risk {
    const v = (s ?? '').trim().toLowerCase();
    if (v.includes('nulo')) return 'Muy bajo';
    if (v === 'muy bajo' || v === 'muy_bajo') return 'Muy bajo';
    if (v === 'bajo') return 'Bajo';
    if (v === 'medio') return 'Medio';
    if (v === 'alto') return 'Alto';
    if (v === 'muy alto' || v === 'muy_alto') return 'Muy alto';
    return 'Medio';
  }

  readonly riskColor = RISK_COLOR;

  chipSolidBg(r: Risk): string {
    return this.riskColor[r] ?? '#9CA3AF';
  }

  chipSolidFg(r: Risk): string {
    const bgColor = this.chipSolidBg(r);
    return getContrastingTextColor(bgColor, this.el.nativeElement);
  }

  private toTableVM(rows: ApiRow[]): RowVM[] {
    const byName = this._groupByName(rows);

    const severityOrder = (rt: Risk): number => {
      const riskSeverity = [
        'Nulo o despreciable',
        'Muy bajo',
        'Bajo',
        'Medio',
        'Alto',
        'Muy alto',
      ];
      return riskSeverity.indexOf(rt);
    };

    const out: RowVM[] = [];
    let counter = 1;

    for (const [name, arr] of byName.entries()) {
      let worstRiskRow: ApiRow | null = null;
      let worstSeverityIndex = -1;

      for (const r of arr) {
        const risk = this.normalizeRisk(r.rating);
        const currentSeverityIndex = severityOrder(risk);
        if (currentSeverityIndex > worstSeverityIndex) {
          worstSeverityIndex = currentSeverityIndex;
          worstRiskRow = r;
        }
      }

      if (worstRiskRow) {
        const risk = this.normalizeRisk(worstRiskRow.rating);
        const respondents = Number(worstRiskRow.totalRespondents ?? 0);
        const score = Number(worstRiskRow.averageScore ?? 0);

        const vm: RowVM = {
          name,
          score,
          rating: risk,
          respondents,
          idx: counter,
        };
        out.push(vm);
        counter++;
      }
    }

    return out.sort((a, b) => {
      const severityA = severityOrder(a.rating);
      const severityB = severityOrder(b.rating);
      if (severityA !== severityB) {
        return severityB - severityA;
      }
      return a.name.localeCompare(b.name);
    });
  }

  trackByRow = (_: number, it: RowVM) => `${it.name}|${it.idx}`;

  capitalizeFirstLetter(val: string) {
    return String(val).charAt(0).toUpperCase() + String(val).slice(1);
  }
}
